import React from 'react';
import { useHistory } from 'react-router-dom';
import { IconButton, Button, Box, Tooltip } from '@mui/material';
import { Grid } from '@mui/material';
import { Delete } from '@mui/icons-material';
import GridContainer from 'components/GridContainer';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import FieldRow from 'components/FormElements/FieldRow';
import { NumericFormat } from 'react-number-format';
import AppTextInput from 'components/FormElements/AppTextInput';
import { InfoOutlined } from '@mui/icons-material';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import { Row } from 'components/Layout';
import { useShareSplits } from 'hooks/data/company/useShareSplits';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type RevaluationRowsProps = {
  revaluations: Array<Record<string, any>>;
  setRevaluations: (x: Array<Record<string, any>>) => void;
  errorTextRevaluation: Array<Record<string, any>>;
  companyId: string;
  isDuplicate: boolean;
  disabled?: boolean;
};

const RevaluationRows: React.FC<RevaluationRowsProps> = ({
  revaluations,
  setRevaluations,
  errorTextRevaluation,
  companyId,
  isDuplicate,
  disabled = false,
}) => {
  const history = useHistory();
  const { shareSplits } = useShareSplits({ params: { companyId } });
  const [canCreateShareSplit] = useHasPermissions([
    RolePermission.CreateShareSplit,
  ]);

  const handleInputChange = (e, index, isDate) => {
    if (isDate) {
      const list = [...revaluations];
      list[index].date = e;
      setRevaluations(list);
    } else {
      const { name, value } = e;
      const list = [...revaluations];
      list[index][name] = value;
      setRevaluations(list);
    }
  };

  const handleRemoveRow = (index) => {
    const list = [...revaluations];
    list.splice(index, 1);
    setRevaluations(list);
  };

  const handleAddRow = () => {
    setRevaluations([
      ...revaluations,
      {
        amount: '',
        date: new Date(),
      },
    ]);
  };

  const isDisabled = (revaluation) =>
    disabled ||
    revaluation.setVia === 'EXIT' ||
    revaluation.setVia === 'EXIT_AMENDMENT' ||
    revaluation.setVia === 'SHARE_CONSIDERATION_EXIT' ||
    revaluation.setVia === 'SHARE_CONSIDERATION_EXIT_AMENDMENT' ||
    revaluation.setVia === 'SHARE_SPLIT' ||
    revaluation.setVia === 'ALLOCATION_WIZARD' ||
    revaluation.setVia === 'INVESTOR_REPORTING';

  const setViaToLabel = (type: string, shareSplitId?: string) => {
    const shareSplitFactor = shareSplitId
      ? shareSplits?.data?.find((split) => split._id === shareSplitId)
          ?.shareSplitFactor
      : '';

    switch (type) {
      case 'DIRECT':
        return 'Set in the company page';
      case 'INVESTOR_REPORTING':
        return 'Set via the update function';
      case 'EXIT':
        return 'Set via an exit';
      case 'EXIT_AMENDMENT':
        return 'Set via an edited exit';
      case 'SHARE_SPLIT':
        return `Set via a share split (Factor: ${shareSplitFactor})`;
      case 'ALLOCATION_WIZARD':
        return 'Set via a new allocation';
      case 'SHARE_CONSIDERATION_EXIT':
        return 'Set via share consideration exit';
      case 'SHARE_CONSIDERATION_EXIT_AMENDMENT':
        return 'Set via share consideration exit';
      default:
        return '';
    }
  };

  return (
    <>
      {revaluations?.map((x, i) => {
        return (
          <FieldRow
            key={i}
            title={`Revaluation ${i + 1}`}
            centerTitle
            informationText={setViaToLabel(x.setVia, x.shareSplitId)}
          >
            <GridContainer alignItems="center">
              <Grid size={{ md: 5 }}>
                <NumericFormat
                  onValueChange={({ value }) => {
                    handleInputChange(
                      {
                        name: 'amount',
                        value,
                      },
                      i,
                      false,
                    );
                  }}
                  required
                  disabled={isDisabled(x)}
                  name="amount"
                  placeholder="Enter amount"
                  value={x.amount}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  prefix="£"
                  customInput={AppTextInput}
                  error={errorTextRevaluation[i]?.amount}
                  helperText={errorTextRevaluation[i]?.amount}
                />
              </Grid>

              <Grid size={{ md: 5 }}>
                <AppDatePicker
                  required
                  name="date"
                  value={new Date(x.date)}
                  onChange={(e) => handleInputChange(e, i, true)}
                  error={errorTextRevaluation[i]?.date}
                  helperText={errorTextRevaluation[i]?.date}
                  disabled={isDisabled(x)}
                  maxDate={new Date()}
                />
              </Grid>

              <Grid size={{ md: 2 }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveRow(i)}
                  disabled={isDisabled(x)}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </GridContainer>
          </FieldRow>
        );
      })}
      <Box display="flex" justifyContent="flex-end">
        {companyId && !isDuplicate && canCreateShareSplit && (
          <Button
            onClick={() => history.push(createShareSplitAddRoute(companyId))}
            variant="text"
            color="primary"
          >
            <Tooltip
              arrow
              title="If there has been a share split since the company's last share price update, please process this in the share split function before adding a new revaluation."
            >
              <span>
                <Row centered spacing="xs">
                  Split shares <InfoOutlined fontSize="small" />
                </Row>
              </span>
            </Tooltip>
          </Button>
        )}
        {!disabled && (
          <Button onClick={handleAddRow} variant="text" color="primary">
            Add revaluation
          </Button>
        )}
      </Box>
    </>
  );
};

export default RevaluationRows;
